import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import { withTypes, FieldInputProps } from "react-final-form";
import { useIntl } from "react-intl";
import { standardDateTimeFormat, convertToUTC } from "utils/dateUtils";
import { FORM_ERROR } from "final-form";
import translateError from "utils/translateError";
import FormTextField from "components/FormTextField";
import { isValid } from "date-fns";

import ContextOrganizationField, {
  ContextOrganizationFieldMode,
} from "components/ContextOrganizationField";

export type WebinarFormValues = {
  organizationId: string;
  sku: string;
  title: string;
  eventStartDate: Date;
  eventEndDate: Date;
};

const { Form } = withTypes<WebinarFormValues>();

type WebinarFormProps = {
  organizationPickerMode?: ContextOrganizationFieldMode;
  submitLabel: string;
  onSubmit: (values: WebinarFormValues) => Promise<any>;
  onCancel?: () => void;
};

const TestDataGenerationForm = ({
  organizationPickerMode,
  submitLabel,
  onSubmit,
}: WebinarFormProps) => {
  const { formatMessage: t } = useIntl();

  const submitSave = async (values: WebinarFormValues) => {
    try {
      const updatedValues = { ...values };
      updatedValues.eventStartDate = convertToUTC(values.eventStartDate)!;
      updatedValues.eventEndDate = convertToUTC(values.eventEndDate)!;

      var response = await onSubmit(updatedValues);

      return response;
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const generateSkuFieldsHandler = (
    startDate: Date,
    input: FieldInputProps<any, HTMLElement>,
    change: (
      field: "sku" | "title" | "eventEndDate",
      value?: string | Date
    ) => void
  ) => {
    input.onChange(startDate);

    if (!isValid(startDate)) return;

    const sku = `WTEST-${startDate.getFullYear()}-${
      startDate.getMonth() + 1
    }-${startDate.getDate()}-${startDate.toLocaleTimeString()}`;
    const title = `${sku} - Test webinar`;
    const minutes = 15;
    const endDate = new Date(startDate.getTime() + minutes * 60000);

    change("sku", sku);
    change("title", title);
    change("eventEndDate", endDate);
  };

  return (
    <Form
      onSubmit={submitSave}
      render={({
        form: { change },
        handleSubmit,
        submitting,
        submitError,
        values,
      }) => {
        return (
          <form onSubmit={handleSubmit} noValidate>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <ContextOrganizationField
                  values={values}
                  formChange={change}
                  mode={organizationPickerMode}
                />

                <Grid item xs={6}>
                  <Field name="eventStartDate" required>
                    {({ input, meta }) => (
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <DateTimePicker
                          label={t({ id: "skuFormEventStartDateLabel" })}
                          format={standardDateTimeFormat}
                          ampm={true}
                          slotProps={{
                            textField: {
                              variant: "standard",
                              error: meta.error && meta.submitFailed,
                              helperText:
                                meta.error && meta.submitFailed && meta.error,
                            },
                            actionBar: {
                              actions: ["clear", "cancel"],
                            },
                          }}
                          {...input}
                          value={input.value === "" ? null : input.value}
                          onChange={(date) =>
                            generateSkuFieldsHandler(date, input, change)
                          }
                        />
                      </FormControl>
                    )}
                  </Field>
                </Grid>

                <Grid item xs={6}>
                  <Field name="eventEndDate">
                    {({ input, meta }) => (
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <DateTimePicker
                          label={t({ id: "skuFormEventEndDateLabel" })}
                          format={standardDateTimeFormat}
                          ampm={true}
                          slotProps={{
                            textField: {
                              variant: "standard",
                              error: meta.error && meta.submitFailed,
                              helperText:
                                meta.error && meta.submitFailed && meta.error,
                            },
                            actionBar: {
                              actions: ["clear", "cancel"],
                            },
                          }}
                          {...input}
                          value={input.value === "" ? null : input.value}
                        />
                      </FormControl>
                    )}
                  </Field>
                </Grid>

                <Grid item xs={12}>
                  <FormTextField name="sku" label={t({ id: "skuLabel" })} />
                </Grid>

                <Grid item xs={12}>
                  <FormTextField
                    name="title"
                    label={t({ id: "skuFormTitleLabel" })}
                  />
                </Grid>

                {submitError && (
                  <Box width="100%">
                    <Alert severity="error">{submitError}</Alert>
                  </Box>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button
                variant="contained"
                type="submit"
                color="primary"
                disabled={submitting}
              >
                {submitLabel}
              </Button>
            </DialogActions>
          </form>
        );
      }}
    />
  );
};

export default TestDataGenerationForm;
